import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { MaskedLine, Reveal, SectionHeading } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { images } from "@/content/images";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function About() {
  const { t } = useLang();
  const a = t.about;

  return (
    <>
      <section className="pt-36 sm:pt-44">
        <div className="wrap">
          <motion.p {...fade(0.1)} className="eyebrow flex items-center gap-3">
            <span className="h-px w-10 bg-teak" />
            {a.hero.eyebrow}
          </motion.p>
          <h1 className="mt-6 font-display text-[clamp(2.6rem,6vw,5.6rem)] font-light leading-[1.02] tracking-tight text-espresso">
            <MaskedLine delay={0.25}>{a.hero.line1}</MaskedLine>
            <MaskedLine delay={0.4} className="italic text-teak">
              {a.hero.line2}
            </MaskedLine>
          </h1>
          <motion.p
            {...fade(0.7)}
            className="mt-7 max-w-xl text-sm font-light leading-relaxed text-charcoal/70 sm:text-base"
          >
            {a.hero.sub}
          </motion.p>
        </div>
        <Reveal delay={0.2} className="wrap mt-16">
          <div className="overflow-hidden rounded-sm" data-testid="about-hero-image">
            <img
              src={images.poolside}
              alt={a.hero.eyebrow}
              loading="lazy"
              className="aspect-[21/9] w-full object-cover"
            />
          </div>
        </Reveal>
      </section>

      <section className="py-20 sm:py-28 lg:py-32" data-testid="about-story">
        <div className="wrap grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow">{a.story.eyebrow}</p>
            </Reveal>
            <MaskedLine mode="view" className="mt-4">
              <h2 className="font-display text-3xl font-light leading-[1.1] tracking-tight text-espresso sm:text-4xl lg:text-5xl">
                {a.story.title}
              </h2>
            </MaskedLine>
          </div>
          <div className="lg:col-span-7 lg:pt-6">
            <Reveal>
              <p className="text-base font-light leading-relaxed text-charcoal/80 sm:text-lg">
                {a.story.p1}
              </p>
              <p className="mt-6 text-sm font-light leading-relaxed text-charcoal/70 sm:text-base">
                {a.story.p2}
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="group mt-12 overflow-hidden rounded-sm">
                <img
                  src={images.texture}
                  alt={a.story.caption}
                  loading="lazy"
                  className="aspect-[16/9] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <p className="mt-3 font-grotesk text-[11px] uppercase tracking-[0.2em] text-taupe">
                  {a.story.caption}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-sand/70 bg-beige/50 py-20 sm:py-28" data-testid="about-values">
        <div className="wrap">
          <SectionHeading eyebrow={a.values.eyebrow} title={a.values.title} />
          <div className="mt-14 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {a.values.items.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.08}>
                <div className="border-t border-espresso/15 pt-6 transition-colors duration-300 hover:border-teak">
                  <p className="font-grotesk text-sm tracking-[0.2em] text-teak">{v.n}</p>
                  <h3 className="mt-4 font-display text-2xl font-light text-espresso">{v.t}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-charcoal/65">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 lg:py-32" data-testid="about-materials">
        <div className="wrap">
          <SectionHeading eyebrow={a.materials.eyebrow} title={a.materials.title} />
          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {a.materials.items.map((m, i) => (
              <Reveal key={m.t} delay={i * 0.08}>
                <div className="group" data-testid={`about-material-${i}`}>
                  <div className="overflow-hidden rounded-sm">
                    <img
                      src={images[m.img]}
                      alt={m.t}
                      loading="lazy"
                      className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-light text-espresso sm:text-2xl">
                    {m.t}
                  </h3>
                  <p className="mt-2 text-sm font-light leading-relaxed text-charcoal/65">{m.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
